import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { rangePreset } from "./networkFmt";
import NetworkHeader from "./NetworkHeader";
import NetworkPicker from "./NetworkPicker";
import NetworkFilters from "./NetworkFilters";
import NetworkHeadline from "./NetworkHeadline";
import NetworkServerTable from "./NetworkServerTable";
import NetworkSpendChart from "./NetworkSpendChart";
import NetworkBreakdowns from "./NetworkBreakdowns";
import NetworkCallList from "./NetworkCallList";
import NetworkAccessToggle from "./NetworkAccessToggle";

const START = rangePreset("month");

/** Billing → Network costs: AI spend + reuse charges of every server in the Radar network (all · channel · one server). */
export default function NetworkCostsSection() {
    const [access, setAccess] = useState(null);
    const [sel, setSel] = useState({ scope: "all", channel: null, server: null, dateFrom: START.from, dateTo: START.to, clientId: "" });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [feature, setFeature] = useState("");
    const [tick, setTick] = useState(0);

    useEffect(() => {
        api.get("/admin/billing/network/settings").then((r) => setAccess(r.data)).catch(() => setAccess({ can_view: false }));
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const p = new URLSearchParams({ scope: sel.scope, date_from: sel.dateFrom, date_to: sel.dateTo });
            if (sel.scope === "channel") p.set("channel", sel.channel);
            if (sel.scope === "server") p.set("server", sel.server);
            if (sel.clientId) p.set("client_id", sel.clientId);
            const r = await api.get(`/admin/billing/network/summary?${p.toString()}`);
            setData(r.data);
            setError(null);
        } catch (e) {
            setError(formatApiErrorDetail(e));
        } finally {
            setLoading(false);
        }
    }, [sel]);

    useEffect(() => { if (access?.can_view) load(); }, [access, load, tick]);

    if (!access?.can_view) return null;
    const update = (patch) => setSel((s) => ({ ...s, ...patch }));
    const onRefreshed = (msg) => { toast.success(msg); setTick((t) => t + 1); };

    return (
        <section className="space-y-6 mb-14 nc-fade-in" data-testid="network-costs-section">
            <NetworkHeader sel={sel} loading={loading} onRefreshed={onRefreshed} />
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                <NetworkPicker sel={sel} servers={data?.by_server || []} onChange={update} />
                <NetworkFilters sel={sel} clients={data?.options?.clients || []} onChange={update} />
            </div>
            {error && <div className="border border-red-500/30 bg-red-500/5 text-red-300 text-xs rounded-md p-3" data-testid="network-error">{error}</div>}
            {!data && loading && <div className="text-white/40 text-xs" data-testid="network-loading">Loading network costs…</div>}
            {data && (
                <>
                    <NetworkHeadline data={data} />
                    <NetworkServerTable rows={data.by_server} onSelect={(key) => update({ scope: "server", server: key, channel: null })} />
                    <NetworkSpendChart data={data} />
                    <NetworkBreakdowns data={data} onClient={(id) => update({ clientId: id })} onFeature={setFeature} />
                    <NetworkCallList sel={sel} features={data.options?.features || []} feature={feature} onFeature={setFeature} />
                </>
            )}
            {access.can_edit && <NetworkAccessToggle initial={access} />}
        </section>
    );
}
